import { faMinus, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect, useCallback} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectYearsInfo,
  loadAllSGInfoStart,
  loadSGInfoStart,
  selectSGTemplate,
  selectIsTemplateCached,
  resetSGInfo,
  selectContentClassificationsList,
  selectItemTypes,
  selectDifficultyInfo,
  loadItemCountStart,
  selectQueryPanelsItemCount,
  selectQueryPanelIsLoading,
  selectQueryPanelIsloaded,
  selectQueryPanelsItemCountLoading,
  resetItemCount,
  selectSGInfo
} from '../../redux/queryPanel';
import { selectControls } from '../../redux/controls';

import NQTCheckBox from '../NQTCheckBox/NQTCheckBox';
import './QueryPanel.scss';
import { cloneDeep, isEmpty } from 'lodash';
import { CHECKBOX_GROUP_TYPES, isCHECKBOX_GROUP_TYPES } from '../NQTCheckBox/NQTCheckBox.models';
import { CALCULATOR_TYPE, CONTENT_CLASSIFICATION_CB_TYPE, DIFFICULTY_LEVEL_CB_TYPE, GRADE_CB_TYPE, ITEM_TYPE_TYPE, YEAR_CB_TYPE } from './models/QueryPanel.constants';
import NQTMixedCheckBox from '../NQTMixedCheckBox/NQTMixedCheckBox';
import Spinner from '../Spinner/Spinner';
import { IMixedCheckBox } from '../NQTMixedCheckBox/NQTMixedCheckBox.models';
import { CalculatorRequestType, ICalculatorType, IContentClassification, IDifficultyInfo, IGrade, IItemType, ISubjectGradeInfo, IYearsInfo } from '../../shared';
import { loadQueryResultsStart, selectItemRequest, selectTabItemCount } from '../../redux/queryResults';
import { genDDMenuItemLabel, getCalculatorRequestType } from './models/QueryPanel.utils';
import Modal from '../Modals/Modal';
import { WindowType } from '../Modals/models/Modal.types';
import { useLocation } from 'react-router';
import { BookMarkLocationState } from './models';
import { selectSwitchViewSelected, svSelectView } from '../../redux/switchView';
import { LIST_VIEW, QUESTION_VIEW } from '../../redux/switchView/models';
import gaEvents from '../../shared/analyticsEvents';
import { selectControlsloaded } from '../../redux/appSettings';
import { ExplorePanelActionTypes } from '../../redux/explorePanel/models';

const staticMenuStyle = {
  width: '100%'
};
const childStyle = {
  paddingLeft: '5px'
};

function QueryPanel() :JSX.Element {

  const location = useLocation();

  const queryPanelEl = useRef<HTMLDivElement>(null);
  const accordionButton = useRef<HTMLButtonElement>(null);
  // checkboxes
  const inputEl = useRef<HTMLInputElement>(null);
  const inputEl2 = useRef<HTMLInputElement>(null);
  const checkboxEl = useRef<HTMLSpanElement>(null);
  const checkboxEl2 = useRef<HTMLSpanElement>(null);
  const subjectButtonEl = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch();

  //#region Selectors

  const controls = useSelector(selectControls);
  const controlsLoaded = useSelector(selectControlsloaded);
  const qpIsLoading = useSelector(selectQueryPanelIsLoading);
  const qpIsLoaded = useSelector(selectQueryPanelIsloaded);
  // First SGInfo response is cached for fast resets
  const isSGTemplateCached = useSelector(selectIsTemplateCached);
  const SGTemplate = useSelector(selectSGTemplate);
  const SGInfo = useSelector(selectSGInfo);
  const yearsInfo = useSelector(selectYearsInfo);
  const contentClassificationList = useSelector(selectContentClassificationsList);
  const itemTypeList = useSelector(selectItemTypes);
  const difficultyInfoList = useSelector(selectDifficultyInfo);
  const itemCount = useSelector(selectQueryPanelsItemCount);
  const itemCountLoading = useSelector(selectQueryPanelsItemCountLoading);

  const itemRequest = useSelector(selectItemRequest);
  const gridItemCount = useSelector(selectTabItemCount); // should take precedence over itemCount

  const switchViewSelected = useSelector(selectSwitchViewSelected);

  //#endregion Selectors

  //#region State

  // used for ETSBookmarks deep-linking - when toggled off, comp. should behave like normal
  const [populateManually, setPopulateManually] = useState<boolean>( () => {
    if (!isEmpty(location.state)) {
      const bookmarkLocationState = location.state as BookMarkLocationState;
      const isBookmark = bookmarkLocationState.isBookmark;
      window.history.replaceState({}, document.title); // doesnt trigger a re-render
      return isBookmark;
    } else {
      return false;
    }
  });

  const [subjects, setSubjects] = useState<ISubjectGradeInfo[]>(SGInfo?.subjectGradeInfo ? cloneDeep(SGInfo.subjectGradeInfo) : []);
  const [grades, setGrades] = useState<IGrade[]>(() => {
    const foundSub = SGInfo?.subjectGradeInfo?.find(sub => sub.isSelected);
    if (foundSub) {
      return cloneDeep(foundSub.gradeList);
    } else {
      return [];
    }
  });
  const [years, setYears] = useState<IMixedCheckBox<IYearsInfo> | null>(() => {
    if (!!itemRequest && itemRequest.YearsInfo?.length) {
      const yearsClone = cloneDeep(itemRequest.YearsInfo);
      yearsClone.reverse();
      const yearsTree = {
        label: controls?.AllYears?.controlText,
        isSelected: yearsClone.length === 1 ?
          true :
          yearsClone.filter(yr => yr.isSelected).length === yearsClone.length,
        childList: yearsClone
      };
      return yearsTree;
    } else {
      return null;
    }
  });
  const [selectedSubject, setSelectedSubject] = useState<ISubjectGradeInfo|null>(() => {
    if (SGInfo?.subjectGradeInfo) {
      const foundSub = SGInfo?.subjectGradeInfo?.find(sub => sub.isSelected);
      return foundSub ? cloneDeep(foundSub) : null;
    } else {
      return null;
    }
  });
  const [selectedGrades, setSelectedGrades] = useState<IGrade[]>(grades ? grades.filter(gd => gd.isSelected) : []);
  const [selectedYears, setSelectedYears] = useState<IYearsInfo[]>(
    itemRequest && itemRequest.YearsInfo ?
      cloneDeep(itemRequest.YearsInfo.filter(yr => yr.isSelected)) : []
  );
  const [disableYear, setDisableYear] = useState(selectedYears.length > 0 ? false : true);

  const [contentClassifications, setContentClassifications] = useState<IContentClassification[]>(() => {
    if (itemRequest && itemRequest.ContentClassifications && itemRequest.ContentClassifications.contentClassificationList?.length) {
      const cc = cloneDeep(itemRequest.ContentClassifications.contentClassificationList);
      cc.forEach(cc => {
        const ccFound = itemRequest.ContentClassifications?.contentClassificationList?.find( item => item.ccTableID === cc.ccTableID);
        if (ccFound) {
          cc.isSelected = ccFound.isSelected;
          if (cc.isSelected) {
            cc.childList.forEach( itm => itm.isSelected = true);
          } else {
            cc.childList.forEach(itm => {
              const cChildFound = ccFound.childList.find(it => it.ccTableID === itm.ccTableID);
              if (cChildFound) {
                itm.isSelected = cChildFound.isSelected;
              } else {
                itm.isSelected = false;
              }
            });
          }
        }
      });
      return cc;
    } else {
      return [];
    }
  });
  const [itemTypes, setItemTypes] = useState<IMixedCheckBox<IItemType> | null>(() => {
    if (itemTypeList) {
      const itemTypeClone = cloneDeep(itemTypeList);
      let allSelect = false;
      itemTypeClone?.forEach(it => it.isSelected && (allSelect=true));
      const itemTypeTree = {
        label: controls?.AllTypes?.controlText,
        isSelected: allSelect,
        childList: itemTypeClone
      } as IMixedCheckBox<IItemType>;
      return itemTypeTree;
    } else {
      return null;
    }
  });

  const [calcTypes, setCalcTypes] = useState<IMixedCheckBox<ICalculatorType> | null>(null);

  const [difficultyInfo, setDifficultyInfo] = useState<IMixedCheckBox<IDifficultyInfo>| null>(null);

  const [selectedCC, setSelectedCC] = useState<IContentClassification[]>(() => {
    if (contentClassifications.length > 0) {
      return contentClassifications.filter(cc => cc.isSelected || (cc.childList && cc.childList.filter(ch => ch.isSelected).length));
    }
    else {
      return [];
    }
  });
  const [selectedItemTypes, setSelectedItemTypes] = useState<IItemType[]>([]);
  const [selectedDifficultyInfo, setSelectedDifficultyInfo] = useState<IDifficultyInfo[]>([]);
  const [selectedCalcType, setSelectedCalcType] = useState<ICalculatorType[]>([]);
  const [onlyOnline, setOnlyOnline] = useState(false);
  //Flag to reload SGY info when user changes selected year *needed to re-load correct content classifications*
  const [yearCheckBoxChanged, setYearCheckBoxChanged] = useState(false);

  const [gotSGY, setGotSGY] = useState<boolean>(
    !!selectedSubject &&
    selectedGrades.length > 0 &&
    selectedYears.length > 0 ?
      true : false
  );

  // Be valid if user made selections w/o submitting and navigating to
  // a different page
  const [isValid, setIsValid] = useState<boolean>(
    !!selectedSubject &&
    selectedGrades.length > 0 &&
    selectedYears.length > 0 &&
    !itemRequest
  );

  const [selectionsExpanded, setSelectionsExpanded] = useState(true);

  // variables to trap user focus to queryPanel
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [keepFocus, setKeepFocus] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [modalType, setModalType] = useState<WindowType>('DESC');
  const [resetSelections, setResetSelections] = useState<boolean>(!!itemRequest);
  //#endregion State

  //#region Function Callbacks

  /**
   * Focusin Event Handler - activated when user re-expands make-selections
   * and makes new selections
   */
  const onOutsideFocus = useCallback((event: Event) => {
    if (isModalOpen) {
      return;
    }
    const target = event.target as Element;
    if (target && queryPanelEl.current) {
      const {top, right, bottom, left} = queryPanelEl.current.getBoundingClientRect();
      const {x, y} = target.getBoundingClientRect();

      // if focus occur outside queryPanel, raise modal
      if (top > y || bottom < y || right < x || left > x) {
        if (isValid) {
          setModalContent(controls?.SearchApply ? controls?.SearchApply?.controlText : '');
          setModalType('PROMPT');
        } else {
          setModalContent(controls?.SearchWarning ? controls?.SearchWarning?.controlText : '');
          setModalType('DESC');
        }
        setIsModalOpen(true);
      }
    }
  }, [isModalOpen, isValid, controls]);

  /**
   * Updates selectedGrades, selectedYears, selectedContentClassifications
   * selectedItemTypes, and selectedDifficultyLevels on checkbox toggle
   */
  const updateSelected = useCallback(( type: CHECKBOX_GROUP_TYPES, ) => {
    if (isCHECKBOX_GROUP_TYPES(type)) {
      switch (type) {
      case GRADE_CB_TYPE:
        setSelectedGrades(grades.filter(gr => gr.isSelected));
        setHasChanged(true);
        break;
      case YEAR_CB_TYPE:
        years &&
        setSelectedYears(years.childList.filter(yr => yr.isSelected));
        //toggle change to reload SGY to update content classifications
        setYearCheckBoxChanged(!yearCheckBoxChanged);
        setHasChanged(true);
        break;
      case CONTENT_CLASSIFICATION_CB_TYPE:
        setSelectedCC(contentClassifications.filter(cc => cc.isSelected || (cc.childList && cc.childList.filter(ch => ch.isSelected).length)));
        setHasChanged(true);
        break;
      case ITEM_TYPE_TYPE:
        itemTypes &&
        setSelectedItemTypes(itemTypes.childList.filter(it => it.isSelected));
        setHasChanged(true);
        break;
      case DIFFICULTY_LEVEL_CB_TYPE:
        difficultyInfo &&
        setSelectedDifficultyInfo(difficultyInfo.childList.filter(yr => yr.isSelected));
        setHasChanged(true);
        break;
      case CALCULATOR_TYPE:
        calcTypes &&
        setSelectedCalcType(calcTypes.childList.filter(ca => ca.isSelected));
        setHasChanged(true);
        break;
      }
    }
  }, [grades, years, yearCheckBoxChanged, contentClassifications, itemTypes, difficultyInfo, calcTypes]);

  /**
   * onClick Handler for subject dropdown menu
   */
  const dropdownOnClickHandler = useCallback((e: React.FormEvent, field: string, obj: ISubjectGradeInfo) => {
    e.preventDefault();
    if (obj && typeof obj !== null) {
      switch(field) {
      case 'subject':
        if (
          !selectedSubject ||
          (obj.subject !== selectedSubject?.subject || obj.ndeSystemID !== selectedSubject.ndeSystemID)
        ) {
          subjects.forEach(sub => sub.isSelected && (sub.isSelected = false));
          obj.isSelected = true;
          setSelectedSubject(cloneDeep(obj));
          setSelectedGrades([]);

          // Dispatch the action to trigger the saga
          dispatch({
            type: ExplorePanelActionTypes.LOAD_SELECTORS_START,
            payload: {
              SystemID: obj.ndeSystemID,
              SubjectCode: obj.subject,
            },
          });
        }
        break;
      default:
        console.log('Option can not be handled by ddhander');
      }
    }
  }, [selectedSubject, subjects, dispatch]);

  /**
   * getQuestions Handler - turn KeepFocus off (which in turn fires effect and removes onFocus event-listener),
   * then dispatches loadQueryResultsStart, which fetches ListView and QuestionView data
   */
  const getQuestions = useCallback((onlyOnlineFlag: boolean) => {
    if (
      selectedSubject &&
      selectedGrades.length > 0 &&
      selectedYears.length > 0 &&
      selectedSubject &&
      itemTypes &&
      difficultyInfo) {
      // Remove focus lock
      keepFocus && setKeepFocus(false);
      setHasChanged(false);
      // disable Get Questions until next changes
      setIsValid(false);

      const clonedCC = cloneDeep(contentClassifications);
      const clonedItemTypes = cloneDeep(itemTypes.childList);
      const clonedDifficultyInfo = cloneDeep(difficultyInfo.childList);
      const calcType = getCalculatorRequestType(selectedCalcType);

      dispatch(
        loadQueryResultsStart({
          SubjectCode: selectedSubject.subject,
          GradeStr: selectedGrades.reduce(
            (acc: string, curr: IGrade, ind:number ) => {
              return ind === 0 ? acc += `${curr.grade}` : acc += `,${curr.grade}`;
            }, ''
          ),
          SystemID: selectedSubject.ndeSystemID,
          SubjectGradeInfo: [cloneDeep(selectedSubject)],
          YearsInfo: cloneDeep(years?.childList),
          LimitToOnlineItems: onlyOnlineFlag,
          ContentClassifications: { contentClassificationList: clonedCC},
          ItemTypes: clonedItemTypes,
          DifficultyInfo: clonedDifficultyInfo,
          CalculatorInfo: calcType
        })
      );
      if (selectionsExpanded) {
        !accordionButton.current?.classList.contains('collapsed') && accordionButton.current?.click();
      }
      if (switchViewSelected === QUESTION_VIEW) {
        dispatch(svSelectView(LIST_VIEW));
      }
    }
  }, [contentClassifications, difficultyInfo, dispatch, itemTypes, keepFocus, selectedCalcType, selectedGrades, selectedSubject, selectedYears.length, selectionsExpanded, switchViewSelected, years?.childList]);

  // Online-only checkbox handlers
  const checkboxOnChangeHandler = useCallback((e: React.FormEvent<HTMLInputElement>):void => {
    populateManually && setPopulateManually(false);
    setOnlyOnline(e.currentTarget.checked);
    if (
      !selectionsExpanded &&
      selectedSubject &&
      selectedGrades.length > 0 &&
      selectedYears.length > 0
    ) {
      getQuestions(e.currentTarget.checked);
    } else if (selectionsExpanded) {
      setHasChanged(true);
    }
    // set focus
    checkboxEl.current ?
      checkboxEl.current.focus() :
      checkboxEl2.current && checkboxEl2.current.focus();
  }, [getQuestions, populateManually, selectedGrades.length, selectedSubject, selectedYears.length, selectionsExpanded]);

  const checkboxKeyUpHandler = useCallback((e: React.KeyboardEvent):void => {
    if (e.key === ' ' || e.key === 'Enter') {
      if (selectionsExpanded) {
        if (inputEl2.current) {
          setOnlyOnline(!inputEl2.current.checked);
          setHasChanged(true);
        }
      } else {
        if (inputEl.current) {
          setOnlyOnline(!inputEl.current.checked);
          if (
            selectedSubject &&
            selectedGrades.length > 0 &&
            selectedYears.length > 0
          ) {
            getQuestions(!inputEl.current.checked);
          }
        }
      }
    }
  }, [getQuestions, selectedGrades, selectedSubject, selectedYears, selectionsExpanded]);

  /**
   * Expands Accordion when collapsed and sets keepFocus, which fires effect
   * that sets onFocus event-listener
   */
  const setExpanded = useCallback(() => {
    setSelectionsExpanded(true);
    populateManually && setPopulateManually(false);
    if (itemRequest) {
      setKeepFocus(true);
    } else {
      setKeepFocus(false);
    }
  },[itemRequest, populateManually]);

  /**
   * Resets selected subject and selected grade back to previous selection
   * and sets resetSelections to true, which fires an effect that resets
   * the rest of the fields
   */
  const resetToItemRequest = useCallback(()=> {
    if (itemRequest && SGTemplate) {
      const clonedSubjects = cloneDeep(SGTemplate.subjectGradeInfo);
      setSubjects(clonedSubjects);
      const foundSubject = clonedSubjects.find(subj => subj.subject === itemRequest.SubjectCode);
      if (foundSubject) {
        foundSubject.isSelected = true;
        foundSubject.gradeList.forEach(grade => {
          if (itemRequest.GradeStr.includes(grade.grade)) {
            grade.isSelected = true;
          }
        });
        setResetSelections(true);
        setGotSGY(false);
        setSelectedSubject(foundSubject);
        setSelectedGrades(foundSubject.gradeList.filter(gr => gr.isSelected));
      }
    }
  }, [itemRequest, SGTemplate]);

  /**
   * Closes Modal and either applies getQuestions or resets focus to subject button
   * Important note: if event-listener is active, focus must be reset inside queryPanel
   * or event-listener will continuous open Modal
   */
  const onModalClose = useCallback((applyChanges?: boolean) => {
    setIsModalOpen(false);

    // If modalType === 'PROMPT, either apply changes or resetSelections
    // else reset focus back to queryPanel to make valid selections
    if (applyChanges) {
      getQuestions(onlyOnline);
      setKeepFocus(false);
    } else if (modalType === 'PROMPT') {
      setKeepFocus(false);
      resetToItemRequest();
    } else {
      if (subjectButtonEl.current) {
        subjectButtonEl.current?.focus();
      }
    }
  },[getQuestions, modalType, onlyOnline, resetToItemRequest]);

  //#endregion Function Callbacks

  //#region useEffects

  // load all subject grade info on mount, if not already cached
  useEffect(() => {
    if (!isSGTemplateCached) {
      dispatch(loadAllSGInfoStart());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sets available subjects - fires twice on startup, max
  // doesn't fire ever if populateManullay is true
  useEffect(() => {
    if (!populateManually) {
      if (isSGTemplateCached && SGTemplate) {
        setSubjects(
          cloneDeep(SGTemplate.subjectGradeInfo)
        );
      } else {
        setSubjects([]);
        setSelectedSubject(null);
        setGrades([]);
        setSelectedGrades([]);
        setSelectedYears([]);
        setSelectedCalcType([]);
        setGotSGY(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSGTemplateCached, SGTemplate]);

  // Sets grades and selectedgrade
  // Fires when a user selects a new subject
  useEffect(() => {
    if (selectedSubject) {
      setGrades(
        selectedSubject.gradeList
      );
    } else {
      setGrades([]);
    }
    if (!populateManually) {
      // do not reset selectedGrade on resetSelections
      if (!resetSelections && itemRequest) {
        setSelectedGrades([]);
        setSelectedYears([]);
        setDisableYear(true);
        setGotSGY(false);
        setHasChanged(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  // Fires when user has selected a subject and grade
  // Fires when selectedYears checkbox changes
  // Resets SGInfo on new subject change
  useEffect(() => {
    if (!populateManually) {
      if (selectedSubject && selectedGrades.length > 0) {
        dispatch(
          loadSGInfoStart(
            {
              SubjectCode: selectedSubject.subject,
              GradeStr: selectedGrades.reduce(
                (acc: string, curr: IGrade, ind:number ) => {
                  return ind === 0 ? acc += `${curr.grade}` : acc += `,${curr.grade}`;
                }, ''
              ),
              SystemID: selectedSubject.ndeSystemID,
              SelectedYearSamples: selectedYears
            }
          )
        );
        setHasChanged(true);
      } else {
        if (isSGTemplateCached && qpIsLoaded) {
          dispatch(resetSGInfo());
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedGrades, selectedSubject, isSGTemplateCached, yearCheckBoxChanged]);

  // Fires when SGInfoState updates with new yearsInfo
  // i.e. after selectedGrade updates
  // sets Years
  // Note - select 1st year in advance to be reflected in checkbox array
  // Retain selected years if grades are only changed (not subject)
  useEffect(() => {
    if (yearsInfo && yearsInfo.length > 0) {
      const yearsClone = cloneDeep(yearsInfo);
      yearsClone.reverse();

      // selectedYears will be empty if user has changed subject
      if (selectedYears.length) {
        yearsClone.forEach(yr => {
          if (selectedYears.some(sy => sy.year === yr.year)) {
            yr.isSelected = true;
          } else {
            yr.isSelected = false;
          }
        });
        const sYears = cloneDeep(yearsClone.filter(yr => yr.isSelected));

        // edge case: where selectedYears are no longer available
        if (sYears.length) {
          setSelectedYears(sYears);
        } else {
          yearsClone[0].isSelected = true;
          setSelectedYears(
            [yearsClone[0]]
          );
        }
      } else {
        yearsClone[0].isSelected = true;
        setSelectedYears(
          [yearsClone[0]]
        );
      }

      const yearsTree = {
        label: controls?.AllYears?.controlText,
        isSelected: yearsClone.length === 1 ?
          true :
          yearsClone.filter(yr => yr.isSelected).length === yearsClone.length,
        childList: yearsClone
      };

      setYears(yearsTree);

    } else {
      setYears(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsInfo]);

  // enables/disables year btn and populates rest of fields
  useEffect(() => {
    if (years) {
      setDisableYear(false);

      //preserve any selectedCC on years change and update selected.
      //Move this block to a helper function as its used in multiple places?
      if (contentClassificationList){
        const contentClassificationListClone = cloneDeep(contentClassificationList);
        if(selectedCC.length > 0){
          //for each content classification check if it exists in selected cc
          contentClassificationListClone?.forEach( cc => {
            const sccFound = selectedCC.find( scc => scc.ccTableID === cc.ccTableID);

            if (sccFound) {
              cc.isSelected = sccFound.isSelected;
              if (cc.isSelected) {
                cc.childList.forEach( itm => itm.isSelected = true);
              } else {
                cc.childList.forEach( itm => {
                  const cChildFound = sccFound.childList.find( it => it.ccTableID === itm.ccTableID);
                  if (cChildFound){
                    itm.isSelected = cChildFound.isSelected;
                  } else {
                    itm.isSelected = false;
                  }
                });
              }
            }
          });
        }
        setContentClassifications(contentClassificationListClone);
        setSelectedCC(contentClassifications.filter(cc => cc.isSelected || (cc.childList && cc.childList.filter(ch => ch.isSelected).length)));
      }

      //preserve any selected itemtypes on years change and update selected.
      let allitl = false;
      if (itemTypeList) {
        const itemTypeClone = cloneDeep(itemTypeList);

        if(selectedItemTypes.length > 0) {
          allitl = true;
          itemTypeClone?.forEach(i => {
            if(selectedItemTypes.some(si => si.itemType === i.itemType)){
              i.isSelected = true;
            } else {
              i.isSelected = false;
              allitl = false;
            }
          });
          const sitl = cloneDeep(itemTypeClone?.filter(i => i.isSelected));
          setSelectedItemTypes(sitl);
        }

        //check if all selected

        const itemTypeTree = {
          label: controls?.AllTypes?.controlText,
          isSelected: allitl,
          childList: itemTypeClone
        };
        setItemTypes(itemTypeTree);
      }

      //preserve any selected difficultyInfo on years change.
      let alldil = false;
      if (difficultyInfoList) {
        const difficultyInfoClone = cloneDeep(difficultyInfoList);
        if(selectedDifficultyInfo.length > 0) {
          alldil = true;
          difficultyInfoClone?.forEach(di => {
            if(selectedDifficultyInfo.some(sdi => sdi.difficultyType === di.difficultyType)){
              di.isSelected = true;
            } else {
              di.isSelected = false;
              alldil = false;
            }
          });
        }
        const difficultyInfoTree = {
          label: controls?.AllDifficulties?.controlText,
          isSelected: alldil,
          childList: difficultyInfoClone
        };
        setDifficultyInfo(difficultyInfoTree);
      }

      setGotSGY(true);
    } else if (!populateManually) {
      setDisableYear(true);
      setSelectedYears([]);
      setSelectedCC([]);
      setSelectedItemTypes([]);
      setSelectedDifficultyInfo([]);
      setGotSGY(false);
      setSelectedCalcType([]);
    }

    // If accordion closed, do not activate hasChanged listener
    selectionsExpanded ? setHasChanged(true) : setHasChanged(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  // update item count
  // don't fire if populateManually is true
  useEffect(() => {
    if (!populateManually) {
      if (
        selectedSubject &&
        selectedGrades.length > 0 &&
        selectedYears.length > 0 &&
        contentClassifications.length >= 0 &&
        itemTypes &&
        difficultyInfo &&
        selectedCalcType
      ) {
        setIsValid(true);
        const clonedCC = cloneDeep(contentClassifications);
        const clonedItemTypes = cloneDeep(itemTypes.childList);
        const clonedDifficultyInfo = cloneDeep(difficultyInfo.childList);
        const calculatorRequestType : CalculatorRequestType = getCalculatorRequestType(selectedCalcType);

        dispatch(
          loadItemCountStart(
            {
              SubjectCode: selectedSubject.subject,
              GradeStr: selectedGrades.reduce(
                (acc: string, curr: IGrade, ind:number ) => {
                  return ind === 0 ? acc += `${curr.grade}` : acc += `,${curr.grade}`;
                }, ''
              ),
              SystemID: selectedSubject.ndeSystemID,
              SubjectGradeInfo: [selectedSubject],
              YearsInfo: years?.childList,
              LimitToOnlineItems: onlyOnline,
              ContentClassifications: { contentClassificationList: clonedCC},
              ItemTypes: clonedItemTypes,
              DifficultyInfo: clonedDifficultyInfo,
              CalculatorInfo: calculatorRequestType
            }
          )
        );
      } else {
        if (qpIsLoaded && itemCount !== 0) {
          setIsValid(false);
          dispatch(resetItemCount());
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedGrades,
    selectedYears,
    onlyOnline,
    contentClassifications,
    selectedCC,
    itemTypes,
    selectedItemTypes,
    difficultyInfo,
    selectedDifficultyInfo,
    selectedCalcType
  ]);

  // fires when only-online is toggled
  useEffect(() => {
    if (onlyOnline) gaEvents.onGA4OnlineOnly();
    setOnlyOnline(onlyOnline);
  }, [onlyOnline]);

  // loads the caluculator info when controls are loaded
  useEffect(() => {
    if (controls?.TTLCalculator) {
      const calcYesClone = cloneDeep(controls?.TTLCalculatorYes);
      const calcNoClone = cloneDeep(controls?.TTLCalculatorNo);
      const calcTypes = {
        label: controls?.TTLCalculator?.controlText,
        isSelected: false,
        childList:
          [
            { calculatorType: calcYesClone.controlID, calculatorTypeLabel: calcYesClone.controlText, isSelected: false },
            { calculatorType: calcNoClone.controlID, calculatorTypeLabel: calcNoClone.controlText, isSelected: false }
          ]
      } as IMixedCheckBox<ICalculatorType>;
      setCalcTypes(calcTypes);
      setSelectedCalcType([]);
    } else {
      setCalcTypes(null);
    }

  }, [controls, controlsLoaded]);

  // fires when keepFocus is toggled - keeps user locked to queryPanel
  useEffect(() => {
    if (keepFocus && hasChanged) {
      window.addEventListener('focusin', onOutsideFocus);
    } else {
      window.removeEventListener('focusin', onOutsideFocus);
    }
    return () => window.removeEventListener('focusin', onOutsideFocus);

  },[hasChanged, keepFocus, isModalOpen, isValid, onOutsideFocus]);

  // fires when resetSelections is true and sets selectedYear
  useEffect(() => {
    if (resetSelections && itemRequest && gotSGY) {

      // populate selectedYears
      if (years) {
        years.childList.forEach(yr => {
          yr.isSelected = !!itemRequest.YearsInfo?.find(item => item.year == yr.year)?.isSelected;
        });
        const filteredYears = years.childList.filter(yr => yr.isSelected);
        years.isSelected = filteredYears.length === years.childList.length;
        setSelectedYears(filteredYears);
      }

      // populate selectedContentClassifications
      contentClassifications.forEach(cc => {
        const ccFound = itemRequest.ContentClassifications?.contentClassificationList?.find( item => item.ccTableID === cc.ccTableID);
        if (ccFound) {
          cc.isSelected = ccFound.isSelected;
          if (cc.isSelected) {
            cc.childList.forEach( itm => itm.isSelected = true);
          } else {
            cc.childList.forEach(itm => {
              const cChildFound = ccFound.childList.find(it => it.ccTableID === itm.ccTableID);
              if (cChildFound) {
                itm.isSelected = cChildFound.isSelected;
              } else {
                itm.isSelected = false;
              }
            });
          }
        }
      });
      setSelectedCC(contentClassifications.filter(cc => cc.isSelected || (cc.childList && cc.childList.filter(ch => ch.isSelected).length)));

      // populate selectedItemTypes
      if (itemTypes) {
        itemTypes.childList.forEach(it => {
          it.isSelected = !!itemRequest.ItemTypes?.find(item => item.itemType == it.itemType)?.isSelected;
        });
        const filteredItemTypes = itemTypes.childList.filter(it => it.isSelected);
        itemTypes.isSelected = filteredItemTypes.length === itemTypes.childList.length;
        setSelectedItemTypes(filteredItemTypes);
      }

      // populate difficultyLevels
      if (difficultyInfo) {
        difficultyInfo.childList.forEach(dl => {
          dl.isSelected = !!itemRequest.DifficultyInfo?.find(item => item.difficultyType == dl.difficultyType)?.isSelected;
        });
        const filteredDifficultyInfo = difficultyInfo.childList.filter(it => it.isSelected);
        difficultyInfo.isSelected = filteredDifficultyInfo.length === difficultyInfo.childList.length;
        setSelectedDifficultyInfo(filteredDifficultyInfo);
      }

      // set online only checkbox
      setOnlyOnline(!!cloneDeep(itemRequest.LimitToOnlineItems));

      setResetSelections(false);
      setHasChanged(false);
      setKeepFocus(false);

      if (selectionsExpanded) {
        !accordionButton.current?.classList.contains('collapsed') && accordionButton.current?.click();
      }

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemRequest, resetSelections, gotSGY]);

  // fires when populateManually is set
  useEffect(() => {
    if (populateManually) {
      if (selectionsExpanded) {
        !accordionButton.current?.classList.contains('collapsed') && accordionButton.current?.click();
      }
      setHasChanged(false);
      setKeepFocus(false);
    }
  }, [populateManually, selectionsExpanded]);

  //#endregion useEffects

  return (
    <section className="nqt-qp" style={{ display: 'block', marginTop: '1.5rem' }}>
      <Modal
        bodyText={modalContent}
        headerText={controls?.ModalMakeSels?.controlText}
        open={isModalOpen}
        onClose={onModalClose}
        shouldReturnFocusAfterClose={false}
        windowType={modalType}/>
      <div className="accordion accordion-flush row gy-4" id="make-selections-accordion">
        <div className="accordion-it g-0" ref={queryPanelEl}>
          {(!SGTemplate || qpIsLoading) && <Spinner/>}
          <h3 className="accordion-header" id="headingOne">
            <button
              id="nqt-qp--accordion-button"
              className="nqt-qp--accordion-button accordion-button top-accordion"
              ref={accordionButton}
              onClick={
                (e) => !e.currentTarget.classList.contains('collapsed') ?
                  setExpanded() :
                  setSelectionsExpanded(false)
              }
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseOne"
              aria-expanded="true"
              aria-controls="collapseOne">
              {
                selectionsExpanded ?
                  <>
                    <span>
                      {controls?.MakeSelections?.controlText}
                    </span>
                    <FontAwesomeIcon className='fa-minus' icon={faMinus}></FontAwesomeIcon>
                  </>
                  :
                  <>
                    <span>
                      {controls?.RolledUpCriteria?.controlText}
                    </span>
                    <FontAwesomeIcon className='fa-plus' icon={faPlus}></FontAwesomeIcon>
                  </>
              }
            </button>
            {
              !selectionsExpanded &&
              <div className="rolled-up-criteria">
                <div className="rolled-up-criteria--question-total col">
                  <h3 className="nqt-qp--questions-header">
                    {controls?.QuestionTotal?.controlText} &nbsp;
                    {
                      gridItemCount && gridItemCount > -1 && !hasChanged ?
                        gridItemCount :
                        itemCount && itemCount > 0 ?
                          itemCount :
                          0
                    }
                  </h3>
                </div>
                { itemCountLoading && <Spinner/>}
                <div className="rolled-up-criteria--questions-checkbox col">
                  <div className="nqt-qp--checkbox-container">
                    <label className='nqt-qp--questions-checkbox-label' htmlFor='online-only-1'>
                      <input id="online-only-1"
                        className='nqt-checkbox-input'
                        checked={onlyOnline}
                        ref={inputEl}
                        onChange={checkboxOnChangeHandler}
                        type="checkbox"
                        name="only-online-questions"/>
                      <span className="nqt-checkbox"
                        ref={checkboxEl}
                        role="checkbox"
                        aria-checked={onlyOnline}
                        onKeyUp={checkboxKeyUpHandler}
                        tabIndex={0}>
                        <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                      </span>
                      <span className='nqt-checkbox-text'>
                        {controls?.Testable?.controlText}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            }
          </h3>
          <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#make-selections-accordion">
            <div className="accordion-body px-1 py-2">
              <div className="row row-cols-1 gy-4">
                <div className="col">
                  <div className="row row-cols-1 gy-2">
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          ref={subjectButtonEl}
                          disabled={!isSGTemplateCached}
                          type="button"
                          id="dropdownMenuButtonSub"
                          data-bs-toggle="dropdown"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              selectedSubject ?
                                selectedSubject.label :
                                controls?.Subject?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownMenuButtonSub">
                          {genDDMenuItemLabel(controls?.Subject?.controlText)}
                          {
                            subjects.length > 0 &&
                          subjects.map((subObj, ind) => (
                            <li key={`sq-sub-${ind}`}><a className={'dropdown-item' + (subObj.isSelected ? ' selected' : '')} onClick={(e) => dropdownOnClickHandler(e, 'subject', subObj)} href="#">{subObj.label}</a></li>
                          ))
                          }
                        </ul>
                      </div>
                    </div>
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          disabled={!selectedSubject}
                          type="button"
                          id="dropdownMenuButtonGd"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              selectedSubject && selectedGrades.length > 0 ?
                                selectedGrades.reduce(
                                  (acc: string, curr: IGrade, ind:number ) => {
                                    return ind === 0 ? acc += `${curr.gradeLabel}` : acc += `, ${curr.gradeLabel}`;
                                  }, ''
                                ) :
                                (selectedSubject && selectedSubject.ndeSystemID === '3') ?
                                  controls?.Age?.controlText :
                                  controls?.Grade?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownMenuButtonGd">
                          {
                            (selectedSubject && selectedSubject.ndeSystemID === '3') ?
                              genDDMenuItemLabel(controls?.Age?.controlText) :
                              genDDMenuItemLabel(controls?.Grade?.controlText)
                          }
                          {
                            grades.length > 0 &&
                          grades.map((gradObj, ind) => (
                            <li className='li-checkbox-tree' key={`qp-grad-${ind}`}>
                              <NQTCheckBox id={`qp-grad-${ind}`}
                                containerClass="dropdown-item"
                                checkBoxObj={gradObj}
                                label={gradObj.gradeLabel}
                                type={GRADE_CB_TYPE}
                                updateHandler={updateSelected}/>
                            </li>
                          ))
                          }
                        </ul>
                      </div>
                    </div>
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          disabled={disableYear}
                          type="button"
                          id="dropdownMenuButtonYr"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              selectedGrades.length > 0 && selectedYears.length > 0 ?
                                selectedYears.reduce(
                                  (acc: string, curr: IYearsInfo, ind:number ) => {
                                    return ind === 0 ? acc += `${curr.year}` : acc += `, ${curr.year}`;
                                  }, ''
                                ) :
                                controls?.Year?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownMenuButtonYr">
                          {genDDMenuItemLabel(controls?.Year?.controlText)}
                          {
                            (yearsInfo && years) &&
                            <li className='li-checkbox-tree' key={'qp-yr'}>
                              <NQTMixedCheckBox id={'qp-yr'}
                                containerClass="dropdown-item"
                                checkBoxObj={years}
                                childLabelAttribute='year'
                                childStyle={childStyle}
                                label={years.label}
                                type={YEAR_CB_TYPE}
                                updateHandler={updateSelected}/>
                            </li>
                          }
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col nqt-qp--filter">
                  <div className="row row-cols-1 gy-2">
                    <div className="col">
                      <h3 className='nqt-qp--filter-header'>{controls?.FilterBy?.controlText}</h3>
                    </div>
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          type="button"
                          id="dropdownMenuButtonCC"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          disabled={!gotSGY}
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {controls?.HeadCC?.controlText}</span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButtonCC">
                          {genDDMenuItemLabel(controls?.HeadCC?.controlText)}
                          {
                            contentClassificationList && contentClassifications.length > 0 &&
                            contentClassifications.map((ccObj, ind) => (
                              <li className='li-checkbox-tree' key={`qp-cc-${ind}`}>
                                <NQTMixedCheckBox id={`qp-cc-${ind}`}
                                  childLabelAttribute='contentCategoryName'
                                  containerClass="dropdown-item"
                                  checkBoxObj={ccObj}
                                  label={`${ccObj.contentCategoryName}`}
                                  type={CONTENT_CLASSIFICATION_CB_TYPE}
                                  updateHandler={updateSelected}/>
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    </div>
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          type="button"
                          id="dropdownMenuButtonIT"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          disabled={!gotSGY}
                          aria-expanded="false">
                          <span className='dropdown-interior'>{controls?.QT?.controlText}</span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButtonIT">
                          {genDDMenuItemLabel(controls?.QT?.controlText)}
                          {
                            (itemTypeList && itemTypes) &&
                              <li className='li-checkbox-tree' key={'qp-it'}>
                                <NQTMixedCheckBox id={'qp-it'}
                                  containerClass="dropdown-item"
                                  checkBoxObj={itemTypes}
                                  childLabelAttribute="itemTypeLabel"
                                  childStyle={childStyle}
                                  label={`${itemTypes.label}`}
                                  type={ITEM_TYPE_TYPE}
                                  updateHandler={updateSelected}/>
                              </li>
                          }
                        </ul>
                      </div>
                    </div>
                    <div className="col">
                      <div className="dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          type="button"
                          id="dropdownMenuButtonDL"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          disabled={!gotSGY}
                          aria-expanded="false">
                          <span className='dropdown-interior'>{controls?.DL?.controlText}</span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownMenuButtonDL">
                          {genDDMenuItemLabel(controls?.DL?.controlText)}
                          {
                            (difficultyInfoList && difficultyInfo) &&
                              <li className='li-checkbox-tree' key={'qp-dl'}>
                                <NQTMixedCheckBox id={'qp-dl'}
                                  containerClass="dropdown-item"
                                  checkBoxObj={difficultyInfo}
                                  childLabelAttribute='difficultyTypeLabel'
                                  childStyle={childStyle}
                                  label={`${difficultyInfo.label}`}
                                  type={DIFFICULTY_LEVEL_CB_TYPE}
                                  updateHandler={updateSelected}/>
                              </li>
                          }
                        </ul>
                      </div>
                    </div>
                    <div className={`col${(!gotSGY || selectedSubject?.subject !== 'MAT') ? ' hidden' : '' }`}  aria-hidden={!gotSGY || selectedSubject?.subject !== 'MAT'} id="nqt-qp--calc-container">
                      <div className='dropdown'>
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          disabled={!gotSGY}
                          type="button"
                          id="dropdownMenuButtonCalc"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              (selectedCalcType.length) ?
                                selectedCalcType.reduce(
                                  (acc: string, curr: ICalculatorType, ind:number ) => {
                                    return ind === 0 ? acc += `${curr.calculatorTypeLabel}` : acc += `, ${curr.calculatorTypeLabel}`;
                                  }, controls?.Calc?.controlText + ' '
                                ) :
                                controls?.Calc?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownMenuButtonGd">
                          {
                            genDDMenuItemLabel(controls?.Calc?.controlText)
                          }
                          {
                            calcTypes && calcTypes.childList && calcTypes &&
                            calcTypes.childList.map((calcObj, ind) => (
                              <li className='li-checkbox-tree' key={`qp-calc-${ind}`}>
                                <NQTCheckBox id={`qp-calc-${ind}`}
                                  containerClass="dropdown-item"
                                  checkBoxObj={calcObj}
                                  label={calcObj.calculatorTypeLabel}
                                  type={CALCULATOR_TYPE}
                                  updateHandler={updateSelected}/>
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="nqt-qp--questions col">
                  <div className="row row-cols-1 gy-2">
                    <div className="nqt-qp--questions-total col">
                      {itemCountLoading && <Spinner/>}
                      <h3 className="nqt-qp--questions-header">
                        {controls?.QuestionTotal?.controlText} &nbsp;
                        {
                          gridItemCount && gridItemCount > -1 && !hasChanged ?
                            gridItemCount :
                            itemCount && itemCount > 0 ?
                              itemCount :
                              0
                        }
                      </h3>
                    </div>
                    <div className="nqt-qp--questions-checkbox col">
                      <div className="nqt-qp--checkbox-container">
                        <label className='nqt-qp--questions-checkbox-label' htmlFor='online-only-2'>
                          <input id="online-only-2"
                            className='nqt-checkbox-input'
                            checked={onlyOnline}
                            ref={inputEl2}
                            onChange={checkboxOnChangeHandler}
                            type="checkbox"
                            name="only-online-questions" />
                          <span className="nqt-checkbox"
                            ref={checkboxEl2}
                            onKeyUp={checkboxKeyUpHandler}
                            role="checkbox"
                            aria-checked={onlyOnline}
                            tabIndex={0}>
                            <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                          </span>
                          <span className='nqt-checkbox-text'>
                            {controls?.Testable?.controlText}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="nqt-qp--questions-total col">
                      <button disabled={!isValid} onClick={() => {getQuestions(onlyOnline); gaEvents.onGA4GetQuestions(selectedSubject?.label);}}
                        className='nqt-submit-button nqt-qp--questions-button'
                        type='submit'>{controls?.Get?.controlText}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default QueryPanel;